import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { type ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ProductType } from "@repo/api";
import { AppBarWrapper, ContractDetailTabs, type TabDef, useIsDesktop, useSwipeTabs } from "@repo/ui";
import { navigate } from "gatsby";
import { type FC, type PropsWithChildren, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useContractTypeContext } from "../../context/ContractTypeContext";
import PensionOverview from "./pension";

type ITab = TabDef | null;

export type TabsProps = PropsWithChildren<{
    tabs: ITab[];
    defaultTab?: number;
    queryParamKey?: string;
    appBar?: ReactNode;
    fixTabViewportWidth?: boolean;
}>;

export const Tabs: FC<TabsProps> = ({
    children,
    tabs,
    defaultTab = 0,
    queryParamKey = "tab", // muze byt randomizovan
    appBar,
    fixTabViewportWidth,
}) => {
    const [tabIndex, setTabIndex] = useState(defaultTab);
    const ai = useAppInsightsContext();

    const filteredTabs = useMemo(() => tabs.filter((tab): tab is TabDef => tab !== null), [tabs]);

    const filteredChildren = useMemo(
        () =>
            (Array.isArray(children) ? children : [children]).filter(
                (child): child is Exclude<ReactNode, boolean | null | undefined> =>
                    child !== null && child !== undefined && child !== false,
            ),
        [children],
    );

    const handleTabIndexChange = useCallback(
        (newIndex: number) => {
            const url = new URL(window.location.href);
            url.searchParams.set(queryParamKey, filteredTabs[newIndex].slug);

            if (tabIndex !== newIndex) {
                setTabIndex(newIndex);
                window.scrollTo(0, 0);
                navigate(url.pathname + url.search + url.hash, { replace: true });
            }
        },
        [filteredTabs, queryParamKey, tabIndex],
    );

    // Application Insights: rename pageview to include the active tab label
    useEffect(() => {
        // ai.core is typed as non-nullable but can be null before AppInsights initialises
        const aiCore = ai.core as typeof ai.core | null;
        if (!aiCore) return;
        const customTelemetry = aiCore.addTelemetryInitializer((item: ITelemetryItem) => {
            if (item.baseType === "PageviewData" || item.baseType === "PageviewPerformanceData") {
                if (item.baseData) {
                    item.baseData.name = `${document.title} ${filteredTabs[tabIndex].label}`;
                    const newUrl = window.location.pathname + window.location.search.replace(/\d/g, "x");
                    item.baseData.uri = newUrl;
                }
            }
        });
        return () => {
            customTelemetry.remove();
        };
    }, [ai.core, filteredTabs, tabIndex]);

    /**
     * Kouknu do URL, jestli nahodou nema nastaven tab na kterem mam byt zpocatku
     */
    useEffect(() => {
        const url = new URL(window.location.href);
        const indexOfDefaultTab = filteredTabs.findIndex(({ slug }) => slug === url.searchParams.get(queryParamKey));
        // pokud se v URL nachazi nejaky z tabuuu, nastavim ho jako default
        // jinak ZNOVU na prvni pohled zbytecne nastavuje defaultTab — to kvuli optimalizaci
        handleTabIndexChange(indexOfDefaultTab > -1 ? indexOfDefaultTab : defaultTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync the tab only when the available tab list changes.
    }, [filteredTabs]);

    const isDesktop = useIsDesktop();
    const { onTouchStart, onTouchEnd } = useSwipeTabs({
        tabIndex,
        tabCount: filteredTabs.length,
        onSwipe: handleTabIndexChange,
        enabled: !isDesktop,
    });

    const { contractType } = useContractTypeContext();
    const isPension = [ProductType.Uf, ProductType.Pf].includes(
        contractType as typeof ProductType.Uf | typeof ProductType.Pf,
    );

    const headerContent = appBar ? (
        <AppBarWrapper sx={{ display: { md: "block", xs: "none" }, mt: { md: 4 } }}>{appBar}</AppBarWrapper>
    ) : undefined;

    return (
        <ContractDetailTabs
            tabs={filteredTabs}
            tabIndex={tabIndex}
            onTabChange={handleTabIndexChange}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            header={headerContent}
            sideContent={isPension ? <PensionOverview /> : undefined}
            fixTabViewportWidth={fixTabViewportWidth}
        >
            {filteredChildren}
        </ContractDetailTabs>
    );
};
